import React, {useEffect, useContext, createContext} from 'react';

import useCustomReducer from '../reducers/customReducer';
import {defaultData, formSteps} from '../constants/form';
import {init} from '../api/api';
import {formatDateInputReverse} from '../helpers/format-helper';

/* eslint-disable react/prop-types */

const StateContext = createContext();
const DispatchContext = createContext();

const defaultState = {
    loading: true,
    uid: window.location.hash.replace('#', ''),
    data: defaultData,
    userData: null,
    initialData: null,
    currentFormVariant: 'loan',
    currentFormStepIndex: 0,

    // error handling
    showErrorPage: false,
    errorPageMessage: null,
    errorMessage: null,
    successMessage: null,
};

const actionTypes = {
    SET_LOADING: 'SET_LOADING',
    SET_ERROR_PAGE: 'SET_ERROR_PAGE',
    SET_ERROR_MESSAGE: 'SET_ERROR_MESSAGE',
    SET_SUCCESS_MESSAGE: 'SET_SUCCESS_MESSAGE',
    SET_INITIAL_DATA: 'SET_INITIAL_DATA',
    UPDATE_STEP: 'UPDATE_STEP',
    UPDATE_STEP_DATA: 'UPDATE_STEP_DATA',
    SET_UID: 'SET_UID',
    SET_USER_DATA: 'SET_USER_DATA',
};

const DEFAULT_ERROR_MESSAGE = 'Ha ocurrido un error. Vuelva a intentarlo.';

function reducer(state, action) {

    switch (action.type) {

        case actionTypes.SET_LOADING: {
            return {
                ...state,
                loading: action.loading
            };
        }

        case actionTypes.SET_ERROR_PAGE: {
            return {
                ...state,
                showErrorPage: true,
                errorPageMessage: action.message || null,
                errorMessage: null,
                successMessage: null,
                loading: false
            };
        }

        case actionTypes.SET_ERROR_MESSAGE: {
            return {
                ...state,
                errorMessage: action.message,
                successMessage: null
            };
        }

        case actionTypes.SET_SUCCESS_MESSAGE: {
            return {
                ...state,
                successMessage: action.message,
                errorMessage: null
            };
        }

        case actionTypes.SET_INITIAL_DATA: {

            if (state.uid) {
                if (action.initialData.success) {
                    const data = {
                        ...state.data,
                        [formSteps[formSteps.SMS_CODE]]: {
                            userFechaNac: formatDateInputReverse(action.initialData.data.userFechaNac)
                        }
                    };

                    const urlParams = new URLSearchParams(window.location.search);
                    const step = urlParams.get('step');

                    return {
                        ...state,
                        initialData: action.initialData.options,
                        userData: action.initialData.data,
                        data,
                        currentFormStepIndex: step ? parseInt(step) : 1, // step - 1
                        loading: false
                    };
                } else {
                    return {
                        ...state,
                        showErrorPage: true,
                        loading: false
                    };
                }
            }

            return {
                ...state,
                initialData: action.initialData.options,
                loading: false
            };
        }

        case actionTypes.UPDATE_STEP: {
            return {
                ...state,
                loading: false,
                currentFormStepIndex: state.currentFormStepIndex + action.increment,
                showErrorPage: false,
                errorMessage: null,
                successMessage: null,
            };
        }

        case actionTypes.UPDATE_STEP_DATA: {
            const updatedData = {
                ...state.data,
                [action.stepName]: {
                    ...state.data[action.stepName],
                    ...action.stepData
                }
            };

            return {
                ...state,
                loading: false,
                data: updatedData
            };
        }

        case actionTypes.SET_UID: {
            return {
                ...state,
                uid: action.uid
            };
        }

        case actionTypes.SET_USER_DATA: {
            return {
                ...state,
                userData: {
                    ...state.userData,
                    ...action.data
                }
            };
        }

        default: {
            throw new Error(`Unsupported action type: ${action.type}`);
        }

    }
}

export default function DataProvider({children}) {

    const [state, dispatch] = useCustomReducer(reducer, defaultState);

    useEffect(() => {

        async function fetchData() {
            try {
                const initialData = await init(defaultState.uid);
                dispatch({type: actionTypes.SET_INITIAL_DATA, initialData});
            } catch (error) {
                console.error(error);
                dispatch({type: actionTypes.SET_ERROR_PAGE});
            }
        }

        fetchData();

    }, []);

    return (
        <StateContext.Provider value={state}>
            <DispatchContext.Provider value={dispatch}>
                {children}
            </DispatchContext.Provider>
        </StateContext.Provider>
    );

}

export function useDataProviderState() {

    const state = useContext(StateContext);

    if (!state) {
        throw new Error('useDataProviderState must be used within a DataProvider');
    }

    return state;

}

export function useDataProviderActions() {

    const dispatch = useContext(DispatchContext);

    if (!dispatch) {
        throw new Error('useDataProviderActions must be used within a DataProvider');
    }

    function setLoading(loading) {
        dispatch({type: actionTypes.SET_LOADING, loading});
    }

    function nextStep() {
        dispatch({type: actionTypes.UPDATE_STEP, increment: 1});
    }

    function prevStep() {
        dispatch({type: actionTypes.UPDATE_STEP, increment: -1});
    }

    function saveData(stepData, stepName) {
        dispatch({type: actionTypes.UPDATE_STEP_DATA, stepData, stepName});
    }

    function setUid(uid) {
        dispatch({type: actionTypes.SET_UID, uid});
    }

    function setUserData(data) {
        dispatch({type: actionTypes.SET_USER_DATA, data});
    }

    function setErrorMessage(message) {
        if (message !== null) {
            dispatch({type: actionTypes.SET_ERROR_MESSAGE, message: (message || DEFAULT_ERROR_MESSAGE)});
        } else {
            dispatch({type: actionTypes.SET_ERROR_MESSAGE, message: null});
        }
    }

    function setSuccessMessage(message) {
        dispatch({type: actionTypes.SET_SUCCESS_MESSAGE, message});
    }

    function clearMessage() {
        dispatch({type: actionTypes.SET_ERROR_MESSAGE, message: null});
        dispatch({type: actionTypes.SET_SUCCESS_MESSAGE, message: null});
    }

    function setErrorPage(message) {
        dispatch({type: actionTypes.SET_ERROR_PAGE, message: (message || DEFAULT_ERROR_MESSAGE)});
    }

    return {
        setLoading,
        nextStep,
        prevStep,
        saveData,
        setUid,
        setUserData,
        setErrorMessage,
        setSuccessMessage,
        clearMessage,
        setErrorPage
    };

}
